import { logger } from '@parrot-software/observability';

import type { IReceiptPort, BrandInfo } from '@onboarding/application/ports';
import type { ReceiptHeader, BrandFiscalChoice } from '@onboarding/domain/value-objects';
import { createReceiptHeader } from '@onboarding/domain/value-objects';
import type { OnboardingHttpClientType } from '@onboarding/infrastructure/api';

const onbLog = logger.forDomain('ONB');

/**
 * Concrete implementation of IReceiptPort.
 * Fetches store brands and persists receipt headers per brand.
 */
export const createReceiptRepository = (props: { httpClient: OnboardingHttpClientType }): IReceiptPort => {
  const { httpClient } = props;

  const getStoreBrands = async (): Promise<BrandInfo[]> => {
    const brands = await httpClient.getStoreBrands();
    return brands.map((b) => ({ uuid: b.uuid, name: b.name }));
  };

  const saveReceiptHeaders = async (defaultHeader: ReceiptHeader, brandChoices: BrandFiscalChoice[]): Promise<void> => {
    const promises = brandChoices.map(async (choice) => {
      const header = choice.useRegisteredData
        ? defaultHeader
        : createReceiptHeader({
            businessName: choice.brandName,
            legalName: choice.customLegalName,
            rfc: choice.customRfc,
            fiscalAddress: choice.customFiscalAddress,
          });

      try {
        await httpClient.updateBrandReceiptHeader(choice.brandUuid, header);
        onbLog.debug('receipt_brand_header_updated', { 'onb.brand_uuid': choice.brandUuid });
      } catch (error) {
        onbLog.error('receipt_brand_header_update_failed', {
          'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
          'error.message': error instanceof Error ? error.message : String(error),
          'onb.brand_uuid': choice.brandUuid,
        });
        throw error;
      }
    });

    await Promise.all(promises);
  };

  return { getStoreBrands, saveReceiptHeaders };
};
